import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV, SITE } from "@/data/site";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [mobileServices, setMobileServices] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMobileServices(false);
    }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    let closeTimer = null;
    const openMenu = () => {
        if (closeTimer) clearTimeout(closeTimer);
        setServicesOpen(true);
    };
    const scheduleClose = () => {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(() => setServicesOpen(false), 260);
    };

    const isActive = (to) =>
        to === "/" ? pathname === "/" : pathname.startsWith(to);

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 left-0 right-0 z-[70] bg-ivory border-b border-line/60"
        >
            <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex items-center justify-between h-[80px] md:h-[100px]">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group -my-4 md:-my-6"
                >
                    <img
                        src="/images/logo/LogoSpazioIsa.webp"
                        alt="Spazio Isa"
                        width="192"
                        height="192"
                        loading="eager"
                        fetchPriority="high"
                        className="h-24 w-24 md:h-40 md:w-40 object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((item) =>
                        item.children ? (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={openMenu}
                                onMouseLeave={scheduleClose}
                            >
                                <button
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    onClick={() => setServicesOpen((v) => !v)}
                                    className={`flex items-center gap-1.5 text-[13px] uppercase tracking-[0.22em] transition-colors ${
                                        servicesOpen
                                            ? "text-raspberry"
                                            : "text-ink hover:text-raspberry"
                                    }`}
                                >
                                    {item.label}
                                    <ChevronDown
                                        size={14}
                                        className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                                <AnimatePresence>
                                    {servicesOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 6 }}
                                            transition={{ duration: 0.22 }}
                                            className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                                            onMouseEnter={openMenu}
                                            onMouseLeave={scheduleClose}
                                        >
                                            <div className="bg-ivory-warm/95 backdrop-blur-xl rounded-3xl border border-line shadow-soft-lg py-3 min-w-[260px] overflow-hidden">
                                                {item.children.map(
                                                    (c, idx) => (
                                                        <Link
                                                            key={c.to}
                                                            to={c.to}
                                                            data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                            className="group flex items-baseline gap-4 px-6 py-3 text-ink hover:bg-rose-powder/50 transition-colors"
                                                        >
                                                            <span className="text-[10px] tabular-nums text-ink-muted">
                                                                0{idx + 1}
                                                            </span>
                                                            <span className="font-display text-lg group-hover:text-raspberry transition-colors">
                                                                {c.label}
                                                            </span>
                                                        </Link>
                                                    ),
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <Link
                                key={item.label}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={`relative text-[13px] uppercase tracking-[0.22em] transition-colors ${
                                    isActive(item.to)
                                        ? "text-raspberry"
                                        : "text-ink hover:text-raspberry"
                                }`}
                            >
                                {item.label}
                                {isActive(item.to) && (
                                    <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-raspberry" />
                                )}
                            </Link>
                        ),
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={SITE.phoneHref}
                        data-testid="nav-phone"
                        className="hidden xl:flex items-center gap-2 text-[13px] tracking-wide text-ink hover:text-raspberry"
                    >
                        <Phone size={14} />
                        {SITE.phone}
                    </a>
                    <Link
                        to="/contatti"
                        data-testid="nav-cta"
                        className="inline-flex items-center gap-2 rounded-full bg-raspberry text-ivory px-6 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-raspberry-deep transition-all shadow-soft hover:shadow-soft-lg"
                    >
                        Prenota
                    </Link>
                </div>

                <button
                    aria-label="Apri menu"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full bg-ivory-warm border border-line text-ink relative z-[80]"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden fixed inset-0 top-[80px] bg-ivory z-[65]"
                        data-testid="nav-mobile-drawer"
                    >
                        <div className="h-full overflow-y-auto pb-24 px-6 pt-6">
                            {NAV.map((item, i) =>
                                item.children ? (
                                    <div
                                        key={item.label}
                                        className="border-b border-line/70"
                                    >
                                        <button
                                            onClick={() =>
                                                setMobileServices((v) => !v)
                                            }
                                            data-testid="nav-mobile-servizi"
                                            className="w-full flex items-center justify-between py-5 text-ink"
                                        >
                                            <span className="flex items-baseline gap-4">
                                                <span className="text-[10px] tabular-nums text-ink-muted">
                                                    0{i + 1}
                                                </span>
                                                <span className="font-display text-3xl">
                                                    {item.label}
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={18}
                                                className={`transition-transform ${mobileServices ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {mobileServices && (
                                                <motion.div
                                                    initial={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        height: "auto",
                                                        opacity: 1,
                                                    }}
                                                    exit={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    className="overflow-hidden pl-10 pb-4"
                                                >
                                                    {item.children.map((c) => (
                                                        <Link
                                                            key={c.to}
                                                            to={c.to}
                                                            data-testid={`nav-mobile-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                            className="block py-3 text-lg font-display text-ink-soft"
                                                        >
                                                            {c.label}
                                                        </Link>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <Link
                                        key={item.label}
                                        to={item.to}
                                        data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                                        className="flex items-baseline gap-4 py-5 text-ink border-b border-line/70"
                                    >
                                        <span className="text-[10px] tabular-nums text-ink-muted">
                                            0{i + 1}
                                        </span>
                                        <span className="font-display text-3xl">
                                            {item.label}
                                        </span>
                                    </Link>
                                ),
                            )}

                            <div className="pt-8 space-y-3">
                                <a
                                    href={SITE.phoneHref}
                                    className="flex items-center gap-3 text-ink text-sm"
                                >
                                    <Phone size={16} className="text-raspberry" />
                                    {SITE.phone} · {SITE.mobile}
                                </a>
                                <Link
                                    to="/contatti"
                                    data-testid="nav-mobile-cta"
                                    className="mt-4 w-full inline-flex items-center justify-center rounded-full bg-raspberry text-ivory px-6 py-4 text-xs uppercase tracking-[0.24em] font-semibold"
                                >
                                    Prenota un appuntamento
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
